import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "../../components/CustomToast";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import {
  DEFAULT_CONCURRENCY,
  MAX_CONCURRENCY,
  buildConcurrencyClearPayload,
  buildConcurrencyPayload,
  extractApiErrorMessage,
  validateConcurrency,
} from "../../lib/offHours";
import { useAdminSettings } from "./use-admin-settings";

const BUTTON_PRIMARY =
  "rounded-lg bg-brand px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-dark disabled:opacity-50";
const BUTTON_SECONDARY =
  "rounded-lg bg-soft px-4 py-2 text-xs font-bold text-ink-2 transition-colors hover:bg-soft/70 disabled:opacity-50";

/**
 * Agent container capacity, the number the autoscaler turns into the ECS task
 * ceiling on each cycle: ceil(concurrency / max_agents_per_container).
 *
 * With the default of one agent per container this reads as the agent ceiling
 * itself, so the label says so rather than leaving an operator to guess.
 */
export function ConcurrencySection() {
  const { data, isLoading, patch } = useAdminSettings();

  const saved = data?.autoscaler?.concurrency;
  const savedValue = typeof saved === "number" ? String(saved) : "";
  const isDefault = typeof saved !== "number";

  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<null | { title: string; body: string; onConfirm: () => void }>(
    null
  );

  // Seed once per stored value, so a refetch cannot clobber a half-typed edit.
  const lastSynced = useRef<string | null>(null);
  useEffect(() => {
    if (lastSynced.current === savedValue) return;
    lastSynced.current = savedValue;
    setValue(savedValue);
  }, [savedValue]);

  const validation = validateConcurrency(value);
  const dirty = validation.ok && validation.value !== saved;

  const save = async (
    payload: Record<string, unknown>,
    successMessage: string,
    failureMessage: string
  ) => {
    setSaving(true);
    try {
      await patch(payload);
      toast.success(successMessage);
    } catch (error) {
      console.error(failureMessage, error);
      toast.error(extractApiErrorMessage(error, failureMessage));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!validation.ok) return;
    setConfirm({
      title: "Confirm concurrency change",
      body: `The autoscaler applies this within about 3 minutes and will allow up to ${validation.value} agent containers. Raising it increases voice capacity and ECS cost.`,
      onConfirm: async () => {
        setConfirm(null);
        await save(
          buildConcurrencyPayload(validation.value!),
          `Concurrency set to ${validation.value}.`,
          "Failed to update concurrency"
        );
      },
    });
  };

  const handleReset = () => {
    setConfirm({
      title: "Reset concurrency",
      body: `This clears the override and hands capacity back to the deployed default of ${DEFAULT_CONCURRENCY}. It takes effect within about 3 minutes.`,
      onConfirm: async () => {
        setConfirm(null);
        await save(
          buildConcurrencyClearPayload(),
          "Concurrency reset to the default",
          "Failed to reset concurrency"
        );
      },
    });
  };

  if (isLoading) {
    return (
      <div className="rounded-xl border border-line bg-elevated p-6 shadow-sm">
        <p className="flex items-center gap-2 text-xs text-ink-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading concurrency…
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-line bg-elevated p-6 shadow-sm">
      <h2 className="text-sm font-bold text-ink">Concurrency</h2>
      <p className="mt-1 text-xs text-ink-2">
        Agent container capacity the autoscaler plans against. Allowed range is 0 to {MAX_CONCURRENCY}.
      </p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-ink">Agent containers</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={MAX_CONCURRENCY}
            step={1}
            aria-label="Agent container concurrency"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="rounded-lg border border-line bg-elevated px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
        </label>
        <div className="flex gap-2 sm:pb-0.5">
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            className={`${BUTTON_PRIMARY} flex items-center gap-2`}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save concurrency
          </button>
          <button onClick={handleReset} disabled={isDefault || saving} className={BUTTON_SECONDARY}>
            Reset to default
          </button>
        </div>
      </div>

      <div className="mt-3 space-y-1">
        {isDefault ? (
          <p className="text-[11px] text-ink-2">
            Not set, using the deployed default of {DEFAULT_CONCURRENCY}.
          </p>
        ) : (
          <p className="text-[11px] text-ink-2">Current concurrency: {saved}.</p>
        )}
        {!validation.ok && value !== savedValue && (
          <p className="text-[11px] font-medium text-amber-600">{validation.error}</p>
        )}
        {validation.ok && validation.value === 0 && (
          <p className="text-[11px] font-medium text-amber-600">
            Zero pins the agent ceiling at zero, so no voice agents will run.
          </p>
        )}
      </div>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          body={confirm.body}
          confirmLabel="Confirm"
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}